/**
 * IndexedDB, wrapped just far enough to be a `SaveBackend`.
 *
 * Hand-rolled rather than a dependency. What is needed is one object store, five operations and a
 * promise around a request, and a library for that would be a runtime dependency carrying an API
 * surface this uses two per cent of. Everything with a rule in it lives above the port, where it
 * can be tested without a browser.
 *
 * IndexedDB's transaction is the atomicity primitive, and `commit` is where that matters: reading
 * the staged record, writing it to the live key and deleting the staging record happen inside one
 * `readwrite` transaction, so a tab closed mid-swap leaves the previous record intact rather than
 * half of the new one.
 *
 * Requests are wrapped rather than transactions, with one exception. A `put` that resolves is not
 * yet a `put` that is durable: the transaction it belongs to has still to commit. `stage` waits on
 * `oncomplete` for that reason, because a staged record that was never really written would make
 * the swap that follows it a lie.
 */

import { detailOf } from '@/save/saveBackendContract';
import type { SaveBackend } from '@/save/saveBackendContract';

const DATABASE_NAME = 'tbd-saves';
const DATABASE_VERSION = 1;

/**
 * One store, keyed by the string the caller passes: a profile id, or one prefixed for staging.
 * The IndexedDB database version is not the save schema version and never tracks it: this one
 * changes when the store layout does, which is a different question from what a record contains.
 */
const STORE_NAME = 'profiles';

export function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

export async function openIndexedDbBackend(): Promise<SaveBackend> {
  const database = await openDatabase();
  return backendOver(database);
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = (): void => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME);
      }
    };

    // A version change from another tab on a newer build. Closing lets that tab proceed instead of
    // deadlocking both, and this tab keeps whatever it already loaded.
    request.onblocked = (): void => {
      reject(new Error('another tab is holding the save database open'));
    };

    request.onsuccess = (): void => {
      resolve(request.result);
    };
    request.onerror = (): void => {
      reject(new Error(`opening the save database failed: ${detailOf(request.error)}`));
    };
  });
}

function backendOver(database: IDBDatabase): SaveBackend {
  const store = (mode: IDBTransactionMode): [IDBObjectStore, IDBTransaction] => {
    const transaction = database.transaction(STORE_NAME, mode);
    return [transaction.objectStore(STORE_NAME), transaction];
  };

  return {
    async read(key: string): Promise<unknown> {
      const [objectStore] = store('readonly');
      const value: unknown = await request(objectStore.get(key));
      return value ?? null;
    },

    async keys(): Promise<readonly string[]> {
      const [objectStore] = store('readonly');
      const keys = await request(objectStore.getAllKeys());
      return keys.filter((key): key is string => typeof key === 'string');
    },

    async stage(key: string, record: unknown): Promise<void> {
      const [objectStore, transaction] = store('readwrite');
      objectStore.put(record, key);
      await committed(transaction);
    },

    async commit(stagingKey: string, liveKey: string): Promise<void> {
      const [objectStore, transaction] = store('readwrite');
      const staged: unknown = await request(objectStore.get(stagingKey));
      if (staged === undefined) {
        transaction.abort();
        throw new Error(`commit: nothing staged under ${stagingKey}`);
      }

      objectStore.put(staged, liveKey);
      // Guarded, because a staging key equal to the live key would delete what was just written.
      if (stagingKey !== liveKey) objectStore.delete(stagingKey);
      await committed(transaction);
    },

    async remove(key: string): Promise<void> {
      const [objectStore, transaction] = store('readwrite');
      objectStore.delete(key);
      await committed(transaction);
    },

    close(): void {
      database.close();
    },
  };
}

function request<T>(pending: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    pending.onsuccess = (): void => {
      resolve(pending.result);
    };
    pending.onerror = (): void => {
      reject(errorOf(pending.error));
    };
  });
}

/**
 * Resolves when the transaction is durable, not when its last request answered.
 *
 * A quota failure surfaces here as often as on the request, because the browser may only discover
 * it has no room while committing. Rejecting with the transaction's own error keeps the
 * `QuotaExceededError` name intact for `isQuotaExceeded` upstream.
 */
function committed(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = (): void => {
      resolve();
    };
    transaction.onerror = (): void => {
      reject(errorOf(transaction.error));
    };
    transaction.onabort = (): void => {
      reject(errorOf(transaction.error));
    };
  });
}

/** The browser's own error where there is one, so its `name` survives the trip upstream. */
function errorOf(error: DOMException | null): Error {
  return error ?? new Error('the save database refused the operation');
}
